import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "ocean";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variantStyles: Record<Variant, string> = {
  primary:
    "bg-ocean-500 text-white hover:bg-ocean-600 shadow-md hover:shadow-lg",
  secondary:
    "bg-sand-100 text-sand-800 hover:bg-sand-200 border border-sand-300 dark:bg-sand-800 dark:text-sand-100 dark:border-sand-700 dark:hover:bg-sand-700",
  ghost:
    "bg-transparent text-sand-600 hover:text-sand-900 hover:bg-sand-100 dark:text-sand-400 dark:hover:text-sand-100 dark:hover:bg-sand-800",
  danger:
    "bg-coral-50 text-coral-600 hover:bg-coral-100 border border-coral-200 dark:bg-coral-900/20 dark:text-coral-400 dark:border-coral-800",
  ocean:
    "gradient-ocean text-white shadow-lg hover:shadow-xl hover:brightness-110",
};

const sizeStyles: Record<Size, string> = {
  sm: "h-8 px-3 text-sm gap-1.5 rounded-lg",
  md: "h-10 px-4 text-sm gap-2 rounded-xl",
  lg: "h-12 px-6 text-base gap-2.5 rounded-xl",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center font-medium",
        "transition-all duration-200 cursor-pointer",
        "disabled:opacity-40 disabled:pointer-events-none",
        "active:scale-[0.98]",
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  ),
);

Button.displayName = "Button";
